import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import {
  getTokenData,
  isTokenValid,
  refreshAccessToken,
  removeTokenData,
  saveTokenData,
} from '../../services/auth/AuthService';
import { TokenData, User } from '../../models/auth/TokenData';
import Loader from '../../components/common/loader/Loader';
import useDataLayer, { ILoginDataLayer } from '../../hooks/global/UseDataLayer';
import { useAccount } from '../account/AccountContexts';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (tokenData: TokenData) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { refetchData } = useAccount();

  const [isAuthenticated, setAuthenticated] = useState(false);
  const [isInitialize, setInitialize] = useState(false);
  const { pushDataLayer } = useDataLayer();

  useEffect(() => {
    const init = async () => {
      const tokenData = getTokenData();

      if (tokenData && !isTokenValid(tokenData)) {
        refreshAccessToken()
          .then((newTokenData) => {
            saveTokenData(newTokenData);
            checkAuthenticated();
          })
          .catch(() => {
            removeTokenData();
            window.location.href = '/login';
          });
        return;
      }

      checkAuthenticated();
    };

    init();
  }, []);

  const checkAuthenticated = () => {
    const tokenData = getTokenData();

    if (tokenData && isTokenValid(tokenData)) {
      setAuthenticated(true);
      sendLoginEvent(tokenData.user);
    } else {
      removeTokenData();
      setAuthenticated(false);
    }

    setInitialize(true);
  };

  const login = (tokenData: TokenData) => {
    saveTokenData(tokenData);
    setAuthenticated(true);
    sendLoginEvent(tokenData.user);
    localStorage.removeItem('magicLinkCooldownUntil');
    localStorage.removeItem('forgotCooldownUntil');
    localStorage.removeItem('resendCooldownUntil');
  };

  const sendLoginEvent = (user: User | null) => {
    if (!user) {
      console.error('Login event failed');
      return;
    }

    pushDataLayer<ILoginDataLayer>({
      event: 'login_successful',
      email_hashed: user.email,
      user_id: user.id.toString(),
    });
  };

  const logout = () => {
    removeTokenData();
    setAuthenticated(false);
    refetchData();
  };

  if (!isInitialize) {
    return <Loader />;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw Error('useAuth must be used with an AuthProvider');
  }

  return context;
};
